import type { Router } from 'vue-router'
import { createI18n } from 'vue-i18n'

import { catalogueFile, mergeCatalogues, type Catalogue } from './catalogues'
import { PLURAL_RULES } from './plurals'
import type { MessageSchema } from './schema'

export { keys } from './keys'
export type { MessageKey, MessageSchema } from './schema'
export { useI18n, type AppTranslate } from './useI18n'

export const DEFAULT_LOCALE: string = 'en'

// I18N.md §3: `en` is bundled eagerly; every other locale is a lazy chunk per catalogue folder.
const eager = {
  ...import.meta.glob<Catalogue>('/src/shared/i18n/en.json', { eager: true, import: 'default' }),
  ...import.meta.glob<Catalogue>('/src/modules/*/i18n/en.json', { eager: true, import: 'default' }),
}
const lazy = import.meta.glob<Catalogue>(
  ['/src/shared/i18n/*.json', '/src/modules/*/i18n/*.json', '!**/en.json', '!**/legacy-map.json'],
  { import: 'default' },
)

type Loader = () => Promise<Catalogue>
/** source (`shared` or a module folder) → locale → loader */
const loaders = new Map<string, Map<string, Loader>>()

/**
 * Indexes the lazy catalogue files by folder and locale. Called once with the bundled files; a unit test passes its
 * own (only `en` exists today, so the lazy path has no real file to load yet) and calls it again without arguments to
 * restore them.
 */
export function setCatalogueFiles(files: Record<string, Loader> = lazy): void {
  loaders.clear()
  for (const [file, load] of Object.entries(files)) {
    const entry = catalogueFile(file)
    if (!entry) continue
    if (!loaders.has(entry.source)) loaders.set(entry.source, new Map())
    loaders.get(entry.source)?.set(entry.locale, load)
  }
}
setCatalogueFiles()

function createAppI18nInstance(options: { placeholderUntranslated: boolean }) {
  return createI18n<[MessageSchema], string, false>({
    legacy: false,
    locale: DEFAULT_LOCALE,
    fallbackLocale: DEFAULT_LOCALE,
    // The merged `en` catalogue is the schema by construction (scripts/gen-i18n-schema.ts reads the same files).
    messages: { en: mergeCatalogues(Object.values(eager)) as unknown as MessageSchema },
    missingWarn: import.meta.env.DEV,
    fallbackWarn: false,
    // VITE_USE_PLACEHOLDER_UNTRANSLATED: a key found in no locale renders `----key-----` (legacy i18nT).
    missing: options.placeholderUntranslated ? (_locale, key) => `----${key}-----` : undefined,
    // the languages with more than two plural forms (plurals.ts)
    pluralRules: PLURAL_RULES,
  })
}

export type AppI18n = ReturnType<typeof createAppI18nInstance>

let instance: AppI18n | null = null
const loaded = new Set<string>()

/** Boot step "i18n en" (wp/shell §4.10): creates the instance with the bundled `en` catalogue. */
export function createAppI18n(options: { placeholderUntranslated: boolean }): AppI18n {
  instance = createAppI18nInstance(options)
  loaded.clear()
  return instance
}

export function getI18n(): AppI18n {
  if (!instance) throw new Error('createAppI18n() has not run')
  return instance
}

export function currentLocale(): string {
  return getI18n().global.locale.value
}

/** Locales that have at least one catalogue file (besides `en`). */
export function availableLocales(): string[] {
  const locales = new Set<string>([DEFAULT_LOCALE])
  loaders.forEach((byLocale) => byLocale.forEach((_, locale) => locales.add(locale)))
  return [...locales].sort()
}

/** Loads the given catalogue folders for one locale (no-op for `en` and for what is already loaded). */
export async function loadCatalogues(locale: string, sources: string[]): Promise<void> {
  if (locale === DEFAULT_LOCALE) return
  const i18n = getI18n()
  await Promise.all(
    sources.map(async (source) => {
      const key = `${source}:${locale}`
      const load = loaders.get(source)?.get(locale)
      if (loaded.has(key) || !load) return
      try {
        i18n.global.mergeLocaleMessage(locale, await load())
        loaded.add(key)
      } catch (error) {
        // A failed chunk leaves the `en` fallback in place (wp/shell OPEN-6 default).
        console.error(`[i18n] could not load ${source}/${locale}`, error)
      }
    }),
  )
}

/**
 * Catalogue folders every page needs: `common.*`, the shell's chrome (navbar, nav, footer — `shell.*`) and the
 * assistant, a global widget outside the routes (`assistant.*`).
 */
const ALWAYS_LOADED = ['shared', 'shell', 'assistant']

/**
 * The catalogue folders a route renders strings from: the module that declared it (`meta.moduleId`, stamped by the
 * collector) plus `meta.catalogues` — the module catalogues of shared components the page mounts (a page hosting
 * `src/shared/documents` lists `documents`, SHARED_CHANGES_QUEUE #8).
 */
export function routeCatalogues(meta: { moduleId?: string; catalogues?: readonly string[] }): string[] {
  const sources = new Set<string>(meta.catalogues ?? [])
  if (meta.moduleId) sources.add(meta.moduleId)
  return [...sources]
}

/**
 * Switches the UI language: loads `shared`, `shell` and the given catalogue folders (a module id, or the route's
 * `routeCatalogues()`), plus every folder already loaded in another locale, so visited modules stay translated; then
 * sets the locale and `<html lang>`.
 */
export async function setLocale(locale: string, catalogues?: string | readonly string[]): Promise<void> {
  const sources = new Set<string>(ALWAYS_LOADED)
  for (const source of typeof catalogues === 'string' ? [catalogues] : (catalogues ?? [])) sources.add(source)
  loaded.forEach((key) => sources.add(key.slice(0, key.lastIndexOf(':'))))
  await loadCatalogues(locale, [...sources])
  getI18n().global.locale.value = locale
  if (typeof document !== 'undefined') document.documentElement.lang = locale
}

/**
 * Loads the entered route's catalogues (`routeCatalogues()`) in the current locale before the route renders
 * (`router.beforeResolve`).
 */
export function installCatalogueLoader(router: Router): void {
  router.beforeResolve(async (to) => {
    const locale = currentLocale()
    const sources = routeCatalogues(to.meta)
    if (locale !== DEFAULT_LOCALE && sources.length) await loadCatalogues(locale, sources)
  })
}
